import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "../site.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && e.msg) || JSON.stringify(e)).join(" ");
  return String(detail);
}

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }) : "—");
const fmtMoney = (cents, cur) => `$${(cents / 100).toLocaleString("en-US")} ${String(cur || "usd").toUpperCase()}`;

export default function AdminDashboard() {
  const [user, setUser] = useState(null);       // null = checking, false = logged out
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [leads, setLeads] = useState([]);
  const [payments, setPayments] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [tab, setTab] = useState("leads");

  const loadData = useCallback(async () => {
    const [l, p, b] = await Promise.all([
      fetch(`${API}/admin/leads`, { credentials: "include" }),
      fetch(`${API}/admin/payments`, { credentials: "include" }),
      fetch(`${API}/admin/bookings`, { credentials: "include" }),
    ]);
    if (l.status === 401 || p.status === 401 || b.status === 401) { setUser(false); return; }
    setLeads((await l.json()).leads || []);
    setPayments((await p.json()).payments || []);
    setBookings((await b.json()).bookings || []);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/auth/me`, { credentials: "include" });
        if (res.ok) { setUser(await res.json()); await loadData(); }
        else setUser(false);
      } catch { setUser(false); }
    })();
  }, [loadData]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({ email, password }),
      });
      if (!res.ok) { setError(formatApiErrorDetail((await res.json().catch(() => ({}))).detail)); return; }
      setUser(await res.json());
      await loadData();
    } catch (err) { setError("Could not reach the server."); }
  };

  const logout = async () => {
    await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    setUser(false); setLeads([]); setPayments([]);
  };

  const download = (path) => { window.location.href = `${API}${path}`; };

  const decideBooking = async (bookingId, decision) => {
    try {
      const res = await fetch(`${API}/admin/bookings/${bookingId}/decision`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({ decision }),
      });
      if (!res.ok) throw new Error("failed");
      const data = await res.json();
      if (!data.email_sent) alert("Status saved, but the email to the visitor could not be sent — please email them directly.");
      await loadData();
    } catch (e) { alert("Could not save the decision. Please try again."); }
  };

  if (user === null) return <div className="pay-page"><div className="container"><div className="pay-card"><p>Checking your session…</p></div></div></div>;

  if (user === false) {
    return (
      <div className="pay-page">
        <div className="container">
          <div className="pay-card" data-testid="admin-login-card" style={{textAlign:'left'}}>
            <h1 style={{textAlign:'center'}}>Owner sign-in</h1>
            <p style={{textAlign:'center'}}>This area is for Digital Static LLC only.</p>
            <form onSubmit={login}>
              <label className="field-label" htmlFor="adminEmail" style={{color:'#475569'}}>Email</label>
              <input className="input-text" id="adminEmail" data-testid="admin-email" type="email" required autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} />
              <label className="field-label" htmlFor="adminPassword" style={{color:'#475569',marginTop:'14px'}}>Password</label>
              <input className="input-text" id="adminPassword" data-testid="admin-password" type="password" required autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
              {error && <div className="pay-status error" data-testid="admin-error" style={{marginTop:'14px'}}>{error}</div>}
              <button className="btn btn-primary" data-testid="admin-login-submit" type="submit" style={{width:'100%',marginTop:'18px'}}>Sign in ↗</button>
            </form>
            <p style={{textAlign:'center',marginTop:'18px'}}><Link to="/" style={{color:'#0f766e'}}>← Back to the website</Link></p>
          </div>
        </div>
      </div>
    );
  }

  const paidCount = payments.filter((p) => p.payment_status === "paid").length;
  const paidTotal = payments.filter((p) => p.payment_status === "paid").reduce((s, p) => s + (p.amount || 0), 0);

  return (
    <div style={{minHeight:'100vh',background:'var(--bg)'}}>
      <header className="nav">
        <div className="container nav-inner">
          <Link to="/" className="logo">
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true"><circle cx="16" cy="16" r="14" stroke="#14b8a6" strokeWidth="2"/><path d="M11 16H21M16 11V21" stroke="#38bdf8" strokeWidth="2"/></svg>
            <span className="logo-text">Digital Static<small>Owner Dashboard</small></span>
          </Link>
          <div style={{display:'flex',alignItems:'center',gap:'18px'}}>
            <span className="mono-label" style={{color:'#94a3b8',fontSize:'10px'}}>{user.email}</span>
            <button className="btn btn-primary" data-testid="admin-logout" onClick={logout}>Sign out</button>
          </div>
        </div>
      </header>
      <main className="section" style={{paddingTop:'48px'}} data-testid="admin-dashboard">
        <div className="container">
          <div className="section-head-split" style={{marginBottom:'36px'}}>
            <div>
              <span className="mono-label kicker-teal">Owner area</span>
              <h2>Inquiries &amp; pilot payments</h2>
            </div>
            <p>Every website inquiry and pilot checkout, newest first. Export any list to CSV for your records.</p>
          </div>

          <div style={{display:'flex',gap:'16px',flexWrap:'wrap',marginBottom:'28px'}}>
            <div className="approach-card" style={{flex:'1',minWidth:'180px'}}><div className="number">INQUIRIES</div><h3 style={{fontSize:'30px'}} data-testid="stat-leads">{leads.length}</h3></div>
            <div className="approach-card" style={{flex:'1',minWidth:'180px'}}><div className="number">CONSULTATIONS</div><h3 style={{fontSize:'30px'}} data-testid="stat-bookings">{bookings.length}</h3></div>
            <div className="approach-card" style={{flex:'1',minWidth:'180px'}}><div className="number">PAID PILOTS</div><h3 style={{fontSize:'30px'}} data-testid="stat-paid">{paidCount}</h3></div>
            <div className="approach-card" style={{flex:'1',minWidth:'180px'}}><div className="number">PILOT REVENUE</div><h3 style={{fontSize:'30px'}} data-testid="stat-revenue">{fmtMoney(paidTotal, "usd")}</h3></div>
          </div>

          <div style={{display:'flex',gap:'10px',marginBottom:'20px',flexWrap:'wrap'}}>
            <button className={`btn ${tab === "leads" ? "btn-dark" : "btn-outline"}`} data-testid="tab-leads" onClick={() => setTab("leads")}>Inquiries ({leads.length})</button>
            <button className={`btn ${tab === "bookings" ? "btn-dark" : "btn-outline"}`} data-testid="tab-bookings" onClick={() => setTab("bookings")}>Consultations ({bookings.length})</button>
            <button className={`btn ${tab === "payments" ? "btn-dark" : "btn-outline"}`} data-testid="tab-payments" onClick={() => setTab("payments")}>Payments ({payments.length})</button>
            <span style={{flex:1}}></span>
            {tab === "leads" && <button className="btn btn-outline" data-testid="export-leads" onClick={() => download("/admin/export/leads.csv")}>Export inquiries CSV ↓</button>}
            {tab === "bookings" && <button className="btn btn-outline" data-testid="export-bookings" onClick={() => download("/admin/export/bookings.csv")}>Export consultations CSV ↓</button>}
            {tab === "payments" && <button className="btn btn-outline" data-testid="export-payments" onClick={() => download("/admin/export/payments.csv")}>Export payments CSV ↓</button>}
          </div>

          {tab === "leads" && (
            <div style={{overflowX:'auto',background:'#fff',border:'1px solid var(--line)',borderRadius:'12px'}} data-testid="leads-table">
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:'13.5px',fontFamily:'var(--f-body)'}}>
                <thead><tr style={{textAlign:'left',borderBottom:'2px solid var(--line)',fontFamily:'var(--f-mono)',fontSize:'11px',textTransform:'uppercase',letterSpacing:'.08em',color:'#64748b'}}>
                  <th style={{padding:'14px 16px'}}>Received</th><th style={{padding:'14px 16px'}}>Name</th><th style={{padding:'14px 16px'}}>Email</th><th style={{padding:'14px 16px'}}>Organization</th><th style={{padding:'14px 16px'}}>Interest</th><th style={{padding:'14px 16px'}}>Message</th>
                </tr></thead>
                <tbody>
                  {leads.length === 0 && <tr><td colSpan={6} style={{padding:'28px 16px',color:'#94a3b8'}}>No inquiries yet — new website submissions will appear here.</td></tr>}
                  {leads.map((l) => (
                    <tr key={l.lead_id} style={{borderBottom:'1px solid var(--line)',verticalAlign:'top'}}>
                      <td style={{padding:'12px 16px',whiteSpace:'nowrap'}}>{fmtDate(l.created_at)}</td>
                      <td style={{padding:'12px 16px',fontWeight:600}}>{l.name}</td>
                      <td style={{padding:'12px 16px'}}><a href={`mailto:${l.email}`} style={{color:'#0f766e'}}>{l.email}</a></td>
                      <td style={{padding:'12px 16px'}}>{l.company || "—"}</td>
                      <td style={{padding:'12px 16px',whiteSpace:'nowrap'}}>{l.interest}</td>
                      <td style={{padding:'12px 16px',maxWidth:'340px'}}>{l.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === "bookings" && (
            <div style={{overflowX:'auto',background:'#fff',border:'1px solid var(--line)',borderRadius:'12px'}} data-testid="bookings-table">
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:'13.5px',fontFamily:'var(--f-body)'}}>
                <thead><tr style={{textAlign:'left',borderBottom:'2px solid var(--line)',fontFamily:'var(--f-mono)',fontSize:'11px',textTransform:'uppercase',letterSpacing:'.08em',color:'#64748b'}}>
                  <th style={{padding:'14px 16px'}}>Requested at</th><th style={{padding:'14px 16px'}}>Name</th><th style={{padding:'14px 16px'}}>Email</th><th style={{padding:'14px 16px'}}>Preferred slot (ET)</th><th style={{padding:'14px 16px'}}>Focus</th><th style={{padding:'14px 16px'}}>Decision</th>
                </tr></thead>
                <tbody>
                  {bookings.length === 0 && <tr><td colSpan={6} style={{padding:'28px 16px',color:'#94a3b8'}}>No consultation requests yet — they'll appear here.</td></tr>}
                  {bookings.map((b) => (
                    <tr key={b.booking_id} style={{borderBottom:'1px solid var(--line)',verticalAlign:'top'}}>
                      <td style={{padding:'12px 16px',whiteSpace:'nowrap'}}>{fmtDate(b.created_at)}</td>
                      <td style={{padding:'12px 16px',fontWeight:600}}>{b.name}</td>
                      <td style={{padding:'12px 16px'}}><a href={`mailto:${b.email}`} style={{color:'#0f766e'}}>{b.email}</a></td>
                      <td style={{padding:'12px 16px',whiteSpace:'nowrap'}}><span className="outcome-chip" style={{fontSize:'10px'}}>{b.date} · {b.time}</span></td>
                      <td style={{padding:'12px 16px',maxWidth:'300px'}}>{b.note || "—"}</td>
                      <td style={{padding:'12px 16px',whiteSpace:'nowrap'}}>
                        {b.status === "requested" ? (
                          <span style={{display:'flex',gap:'8px'}}>
                            <button className="btn btn-primary" style={{padding:'8px 14px',fontSize:'10px'}} data-testid={`confirm-${b.booking_id}`} onClick={() => decideBooking(b.booking_id, "confirmed")}>Confirm ✓</button>
                            <button className="btn btn-outline" style={{padding:'8px 14px',fontSize:'10px'}} data-testid={`decline-${b.booking_id}`} onClick={() => decideBooking(b.booking_id, "declined")}>Decline</button>
                          </span>
                        ) : (
                          <span className="outcome-chip" style={{fontSize:'10px', ...(b.status === "declined" ? {borderColor:'#fca5a5',color:'#b42318',background:'#fef2f2'} : {})}} data-testid={`status-${b.booking_id}`}>{b.status}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === "payments" && (
            <div style={{overflowX:'auto',background:'#fff',border:'1px solid var(--line)',borderRadius:'12px'}} data-testid="payments-table">
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:'13.5px',fontFamily:'var(--f-body)'}}>
                <thead><tr style={{textAlign:'left',borderBottom:'2px solid var(--line)',fontFamily:'var(--f-mono)',fontSize:'11px',textTransform:'uppercase',letterSpacing:'.08em',color:'#64748b'}}>
                  <th style={{padding:'14px 16px'}}>Created</th><th style={{padding:'14px 16px'}}>Product</th><th style={{padding:'14px 16px'}}>Amount</th><th style={{padding:'14px 16px'}}>Status</th><th style={{padding:'14px 16px'}}>Customer email</th><th style={{padding:'14px 16px'}}>Session</th>
                </tr></thead>
                <tbody>
                  {payments.length === 0 && <tr><td colSpan={6} style={{padding:'28px 16px',color:'#94a3b8'}}>No pilot checkouts yet — payments will appear here.</td></tr>}
                  {payments.map((p) => (
                    <tr key={p.session_id} style={{borderBottom:'1px solid var(--line)',verticalAlign:'top'}}>
                      <td style={{padding:'12px 16px',whiteSpace:'nowrap'}}>{fmtDate(p.created_at)}</td>
                      <td style={{padding:'12px 16px'}}>{p.lookup_key === "pilot_3000" ? "Interactive Pilot" : p.lookup_key}</td>
                      <td style={{padding:'12px 16px',fontWeight:600}}>{fmtMoney(p.amount, p.currency)}</td>
                      <td style={{padding:'12px 16px'}}><span className="outcome-chip" style={{fontSize:'10px'}}>{p.payment_status}</span></td>
                      <td style={{padding:'12px 16px'}}>{p.customer_email || "—"}</td>
                      <td style={{padding:'12px 16px',fontFamily:'var(--f-mono)',fontSize:'11px',color:'#94a3b8'}}>{p.session_id.slice(0, 22)}…</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
